// src/hooks/useStudentsPage.js
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';

/** Small helper to normalize empty filters to null */
const nz = (v) => (v === '' || v === 'all' || v == null ? null : v);

/**
 * Server-side, paginated students fetcher.
 * 
 * Params:
 *  - page (1-based)
 *  - pageSize
 *  - search       (matches full_name OR nric_passport, case-insensitive, contains)
 *  - status       ('Active' | 'Non-Active' | null)
 *  - year         (number | string | null)
 *  - paymentType  ('Full' | 'Individual' | null)
 *  - includeDeleted (boolean) default false
 *
 * Sorting: full_name ASC, id DESC (stable tiebreaker)
 */
export function useStudentsPage({
  page = 1,
  pageSize = 20,
  search = '',
  status = null,
  year = null,
  paymentType = null,
  includeDeleted = false,
}) {
  const params = useMemo(() => {
    return {
      page: Math.max(1, Number(page) || 1),
      pageSize: Math.max(1, Number(pageSize) || 20),
      search: (search ?? '').trim(),
      status: nz(status),
      year: nz(year),
      paymentType: nz(paymentType),
      includeDeleted: Boolean(includeDeleted),
    };
  }, [page, pageSize, search, status, year, paymentType, includeDeleted]);

  const queryKey = useMemo(
    () => ['students_page', params],
    [params]
  );

  const queryFn = async () => {
    const { page, pageSize, search, status, year, paymentType, includeDeleted } = params;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let q = supabase
      .from('students')
      .select(
        `
        id, full_name, nric_passport, email, contact_no,
        enrollment_year, payment_type, status, is_deleted, created_at
        `,
        { count: 'exact' }
      );

    // Soft delete filter
    if (!includeDeleted) q = q.eq('is_deleted', false);

    // Optional filters
    if (status) q = q.eq('status', status);
    if (paymentType) q = q.eq('payment_type', paymentType);
    if (year) q = q.eq('enrollment_year', Number(year));

    // Search across name and NRIC/Passport (contains, case-insensitive)
    if (search) {
      q = q.or(
        `full_name.ilike.%${escapeLike(search)}%,nric_passport.ilike.%${escapeLike(search)}%`
      );
    }

    // Stable sort: name asc, id desc (tie-breaker)
    q = q
      .order('full_name', { ascending: true, nullsFirst: false })
      .order('id', { ascending: false });

    // Pagination
    q = q.range(from, to);

    const { data, error, count } = await q;
    if (error) throw error;

    return {
      rows: data ?? [],
      count: count ?? 0,
      page,
      pageSize,
    };
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn,
    keepPreviousData: true,
    staleTime: 15_000,
  });

  return {
    rows: data?.rows ?? [],
    count: data?.count ?? 0,
    page: data?.page ?? params.page,
    pageSize: data?.pageSize ?? params.pageSize,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Distinct enrollment years (for dropdown)
 */
export function useStudentYears({ includeDeleted = false } = {}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['students_years', { includeDeleted }],
    queryFn: async () => {
      let q = supabase.from('students').select('enrollment_year');

      if (!includeDeleted) q = q.eq('is_deleted', false);

      q = q.order('enrollment_year', { ascending: false });

      const { data, error } = await q;
      if (error) throw error;

      // ✅ Deduplicate + filter null + sort desc
      const years = Array.from(
        new Set(
          (data ?? [])
            .map((r) => r?.enrollment_year)
            .filter((v) => v != null)
        )
      ).sort((a, b) => Number(b) - Number(a));

      return years;
    },
    staleTime: 60_000,
  });

  return {
    years: data ?? [],
    isLoading,
    error,
  };
}

/** Escape % and _ for ilike patterns */
function escapeLike(s) {
  return String(s).replace(/[%_]/g, (m) => '\\' + m);
}
